import React from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { JiraIcon, JiraServiceManagementIcon } from '@atlaskit/logo';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { AnyAri } from '@atlassian/ari/any-ari';
import { ResourceType } from '@atlassian/search-client/third-party-graph-entity';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { RestrictedLozenge } from '../../../../../../../common/ui/restricted-lozenge';
import SearchMetadataLink from '../../../../../../../common/ui/search-metadata-link';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { isNonPublic } from '../../../../../../../common/utils/is-non-public';
import {
	enrichExcerpt,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';

import type {
	searchGraphJiraDc_AtlassianSearchPage$data,
	searchGraphJiraDc_AtlassianSearchPage$key,
} from './__generated__/searchGraphJiraDc_AtlassianSearchPage.graphql';
import { messages } from './messages';
import {
	isSearchGraphJiraDcProject,
	SearchGraphJiraDcProject,
} from './search-graph-jira-dc-project';
import {
	isSearchGraphJiraDcWorkItem,
	SearchGraphJiraDcWorkItem,
} from './search-graph-jira-dc-work-item';

const SERVICE_DESK = 'service_desk';

const isJsmIcon = (containerType: string | null | undefined) => {
	return containerType?.toLowerCase() === SERVICE_DESK;
};

const styles = cssMap({
	footerContainer: {
		paddingTop: token('space.100'),
	},
	typeLabelContainer: {
		'&::first-letter': {
			textTransform: 'capitalize',
		},
	},
});

const PROJECT_SUBTYPES = new Set([
	'software',
	'service_desk',
	'business',
	'project', //to be removed once implementation is finalised
]);

const SUBTYPE_PROJECT = 'project';
const SUBTYPE_SPACE = 'space';
const SUBTYPE_COMMENT = 'comment';
const SUBTYPE_ISSUE = 'issue';

const JIRA_DC_LABELS: Record<string, MessageDescriptor> = {
	[SUBTYPE_SPACE]: messages.space,
	[SUBTYPE_PROJECT]: messages.project,
	[SUBTYPE_COMMENT]: messages.comment,
	[SUBTYPE_ISSUE]: messages.issue,
};

const getLabel = (subtype: string): MessageDescriptor => {
	if (PROJECT_SUBTYPES.has(subtype)) {
		return messages.project;
	}
	return JIRA_DC_LABELS[subtype];
};

export const SearchGraphJiraDc = ({
	node,
	position,
	cloudId,
	searchQuery,
	primaryProduct,
	primarySubProduct,
	appMode,
	product,
	entityType,
	selectedProduct,
	searchSessionId,
	thirdPartyConfigs,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	mainContentWidth,
	selectedSites,
}: SearchGraphResultProps<searchGraphJiraDc_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchGraphJiraDc_AtlassianSearchPage on SearchResultGraphDocument {
				__typename
				id
				integrationId
				subtype
				title
				url
				lastModifiedDate
				excerpt
				permissionLevel
				entity {
					... on ExternalComment {
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalSpace {
								displayName
								url
								subtype
							}
						}
					}
					... on ExternalProject {
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalSpace {
								displayName
								url
								subtype
							}
						}
					}
					... on ExternalWorkItem {
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalSpace {
								displayName
								url
								subtype
							}
						}
					}
				}
				...searchGraphJiraDcProject_AtlassianSearchPage
				...searchGraphJiraDcWorkItem_AtlassianSearchPage
			}
		`,
		node,
	);

	const { formatMessage } = useIntl();

	const { lastModifiedDate, url, permissionLevel, title, excerpt } = data;

	const subtype = data?.subtype || null;

	const integrationId = data.integrationId;

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const jiraIcon = <JiraIcon size="medium" />;
	const jsmIcon = <JiraServiceManagementIcon size="medium" />;
	let icon = jiraIcon;

	// Find the specific datasource for this result based on its integrationId
	const resultDatasource =
		thirdPartyConfigs?.[product]?.datasources?.find((ds) => ds.integrationId === integrationId) ||
		null;
	const site = resultDatasource?.workspaceName || thirdPartyConfigs?.[product]?.workspaceName;

	const entityToLabelMap: Record<string, string> = {
		space: 'space',
		'work-item': 'issue',
		project: 'issue',
		comment: 'comment',
	};

	const resultType = subtype || (entityToLabelMap[entityType] ?? entityType);

	const labelMessage = getLabel(resultType);
	const label = labelMessage ? formatMessage(labelMessage) : resultType;

	const isNotPublic = isNonPublic(permissionLevel);

	const SearchGraphJiraDcResult = isSearchGraphJiraDcProject(data)
		? SearchGraphJiraDcProject
		: isSearchGraphJiraDcWorkItem(data)
			? SearchGraphJiraDcWorkItem
			: null;

	if (SearchGraphJiraDcResult) {
		if (isJsmIcon(data.entity?.container?.subtype)) {
			icon = jsmIcon;
		}
		return (
			<SearchGraphJiraDcResult
				node={data}
				position={position}
				cloudId={cloudId}
				selectedProduct={selectedProduct}
				searchQuery={searchQuery}
				primaryProduct={primaryProduct}
				primarySubProduct={primarySubProduct}
				appMode={appMode}
				searchSessionId={searchSessionId}
				thirdPartyConfigs={thirdPartyConfigs}
				icon={icon}
				product={product}
				subtype={subtype}
				entityType={entityType}
				onCopyLinkButtonClicked={onCopyLinkButtonClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				onResultClicked={onResultClicked}
				onResultContextMenu={onResultContextMenu}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
				mainContentWidth={mainContentWidth}
				selectedSites={selectedSites}
			/>
		);
	}

	let container: JSX.Element | string | null | undefined;
	if (isCommentEntity(data)) {
		const { entity } = data;
		const containerName = entity?.container?.displayName;
		const containerUrl = entity?.container?.url;
		if (isJsmIcon(entity?.container?.subtype)) {
			icon = jsmIcon;
		}
		container =
			containerUrl && containerName ? (
				<SearchMetadataLink label={containerName} href={containerUrl} />
			) : (
				containerName
			);
	} else {
		if (isJsmIcon(subtype)) {
			icon = jsmIcon;
		}
	}

	const headerMetadataParts = [
		['label', <Box xcss={styles.typeLabelContainer}>{label}</Box>],
		['site', site],
		['container', container],
		['last-modified', lastModifiedComponent],
	];

	// space noun (project in the UI) and comment use default result component
	return (
		<SearchResult
			id={data.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			description={processExcerpt(excerpt)}
			icon={icon}
			headerMetadataParts={headerMetadataParts}
			footer={
				isNotPublic && (
					<Box xcss={styles.footerContainer}>
						<RestrictedLozenge restriction={permissionLevel} />
					</Box>
				)
			}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			position={position}
			partial={data}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
		/>
	);
};

const isCommentEntity = (node?: any): node is searchGraphJiraDc_AtlassianSearchPage$data => {
	const { resourceType } = AnyAri.parse(node?.id);
	return resourceType === ResourceType.COMMENT;
};
